/**
 * Creates a throttle+debounce handler for MQTT FETCH messages.
 *
 * Throttle: fires the callback immediately on the first call per type,
 * then blocks further calls for that type for `throttleMs`.
 *
 * Debounce: after each call, schedules a trailing invocation `debounceMs`
 * later — but only if the throttle window has elapsed by the time it fires.
 * This catches the final state after a burst of messages without creating
 * a feedback loop.
 */
export function createFetchThrottle(
  callback: (type: string) => void,
  throttleMs: number,
  debounceMs: number,
  now: () => number = Date.now
) {
  const lastFetchTime: Record<string, number> = {}
  const debounceTimers: Record<string, ReturnType<typeof setTimeout>> = {}

  function handle(type: string) {
    const currentTime = now()
    const lastTime = lastFetchTime[type] ?? -Infinity

    // Throttle: fire immediately if enough time has passed
    if (currentTime - lastTime >= throttleMs) {
      lastFetchTime[type] = currentTime
      callback(type)
    }

    // Debounce: schedule a trailing invocation
    if (debounceTimers[type]) {
      clearTimeout(debounceTimers[type])
    }
    debounceTimers[type] = setTimeout(() => {
      const elapsed = now() - (lastFetchTime[type] ?? -Infinity)
      if (elapsed >= throttleMs) {
        lastFetchTime[type] = now()
        callback(type)
      }
      delete debounceTimers[type]
    }, debounceMs)
  }

  function cleanup() {
    Object.values(debounceTimers).forEach(clearTimeout)
    for (const key of Object.keys(debounceTimers)) {
      delete debounceTimers[key]
    }
  }

  return { handle, cleanup }
}
